import { combineReducers, createAction, createReducer, isAnyOf } from '@reduxjs/toolkit';
import { message } from 'common/services/message';

import api from 'common/api';
import {
  AgreementsFormValues,
  BestOfTempletonAccountFormValues,
  BusinessPartnerInfoFormValues,
  ClientConfirmationFormValues,
  ClientFormValues,
  ClientStatementFormValues,
  CompanyInfoFormValues,
  ConsultancyInitiationFormValues,
  ConsultantConfirmationFormValues,
  FinanceAnalyseFormValues,
  FinancesFormValues,
  HazardProfileFormValues,
  InvestingExperienceFormValues,
  InvestingGoalsFormValues,
  MeetingFormValues,
  RequestInfoFormValues,
} from 'common/models/Forms';
import { Transaction } from 'common/models/Transaction';
import i18n from 'common/services/i18n';

import { startListening, AppListenerEffectApi } from '../../root/store/listener-middleware';
import documents, { DocumentsState, initialDocumentsState } from './features/transaction/ducks';
import verification, { initialVerificationFormState, VerificationFormState } from './features/verification/ducks';

// Re-export extracted modules for backward compatibility
export { InvestingStrategy, RPs, strategiesMap } from './constants';
export {
  selectAgreementBusinessPartnerInfo,
  selectAgreementDone,
  selectAgreementMeeting,
  selectAgreementRequestInfo,
  selectBestOfTempletonAccountInfo,
  selectBestOfTempletonDone,
  selectForm,
  selectFormAgreement,
  selectFormInformations,
  selectFormInformationsDone,
  selectFormInvesting,
  selectFormProtocol,
  selectFormSignatures,
  selectFormTransaction,
  selectInformationsCompanyInfo,
  selectInformationsCustomer1FullName,
  selectInformationsCustomerInfo1,
  selectInformationsCustomerInfo2,
  selectIsCustomerInfo2Visible,
  selectInvestingAgreements,
  selectInvestingAnalyse,
  selectInvestingDone,
  selectInvestingExperience,
  selectInvestingFinances,
  selectInvestingHazard,
  selectInvestingStrategy,
  selectIsFormVerified,
  selectProtocolConsultantConfirmation,
  selectProtocolCustomerConfirmation,
  selectProtocolCustomerStatement,
  selectProtocolDone,
  selectProtocolGoals,
  selectProtocolInitiation,
  selectStrategy,
} from './selectors';
export {
  calculateStrategy,
  getStrategyFromFinances,
  getStrategyFromHazard,
  getStrategyFromInvesting,
  updateProtocolInitiationByStrategy,
} from './strategy';

import { InvestingStrategy } from './constants';
import { calculateStrategy, updateProtocolInitiationByStrategy } from './strategy';
import { selectForm, selectFormTransaction, selectInvestingStrategy } from './selectors';

// Actions
export const setInformationsCompanyInfoValuesAction = createAction<CompanyInfoFormValues>(
  '@@Informations/CompanyInfo/SET',
);
export const setInformationsClient1InfoValuesAction = createAction<ClientFormValues>('@@Informations/Client1Info/SET');
export const setInformationsClient1InfoValuesPartialAction = createAction<Partial<ClientFormValues>>(
  '@@Informations/Client1Info/SET_PARTIAL',
);
export const setInformationsClient2InfoValuesAction = createAction<ClientFormValues>('@@Informations/Client2Info/SET');

export const setInvestingExperienceValuesAction =
  createAction<InvestingExperienceFormValues>('@@Investing/Experience/SET');
export const setInvestingHazardValuesAction = createAction<HazardProfileFormValues>('@@Investing/Hazard/SET');
export const setInvestingFinancesValuesAction = createAction<FinancesFormValues>('@@Investing/Finances/SET');
export const setInvestingAnalyseValuesAction = createAction<FinanceAnalyseFormValues>('@@Investing/Analyse/SET');
export const setInvestingAgreementsValuesAction = createAction<AgreementsFormValues>('@@Investing/Agreements/SET');
export const syncInvestingAgreementsWithProtocolAction = createAction<ConsultantConfirmationFormValues['b']>(
  '@@Investing/Agreements/SYNC_WITH_PROTOCOL',
);
export const setInvestingStrategyAction = createAction('@@Investing/Strategy/SET');

export const setProtocolGoalsValuesAction = createAction<InvestingGoalsFormValues>('@@Protocol/Goals/SET');
export const setProtocolInitiationValuesAction =
  createAction<ConsultancyInitiationFormValues>('@@Protocol/Initiation/SET');
export const updateProtocolInitiationValuesAction = createAction<InvestingStrategy>('@@Protocol/Initiation/UPDATE');
export const setProtocolCustomerStatementValuesAction = createAction<ClientStatementFormValues>(
  '@@Protocol/CustomerStatement/SET',
);
export const setProtocolCustomerConfirmationValuesAction = createAction<ClientConfirmationFormValues>(
  '@@Protocol/CustomerConfirmation/SET',
);
export const setProtocolConsultantConfirmationValuesAction = createAction<ConsultantConfirmationFormValues>(
  '@@Protocol/ConsultantConfirmation/SET',
);

export const setAgreementRequestInfoAction = createAction<RequestInfoFormValues>('@@Agreement/RequestInfo/SET');
export const setAgreementBusinessPartnerInfoAction = createAction<BusinessPartnerInfoFormValues>(
  '@@Agreement/BusinessPartnerInfo/SET',
);
export const setAgreementMeetingAction = createAction<MeetingFormValues>('@@Agreement/Meeting/SET');

export const resetFormAction = createAction('@@Form/RESET');
export const preloadFormStateAction = createAction<FormState>('@@Form/PRELOAD');

export const setBestOfTempletonAccountInfo = createAction<BestOfTempletonAccountFormValues>(
  '@@BestOfTempleton/AccountInfo/SET',
);

export const loadTransactionActions = {
  request: createAction<string>('@@Form/LOAD_TRANSACTION/REQUEST'),
  success: createAction<Transaction>('@@Form/LOAD_TRANSACTION/SUCCESS'),
  failure: createAction('@@Form/LOAD_TRANSACTION/ERROR'),
};

// Reducers
export const initialFormState: FormState = {
  verification: initialVerificationFormState,
  informations: {
    companyInfo: null,
    customerInfo1: null,
    customerInfo2: null,
  },
  investing: {
    experience: null,
    hazard: null,
    finances: null,
    analyse: null,
    agreements: null,
    strategy: null,
  },
  protocol: {
    goals: null,
    initiation: null,
    customerStatement: null,
    customerConfirmation: null,
    consultantConfirmation: null,
  },
  agreement: {
    requestInfo: null,
    businessPartnerInfo: null,
    meeting: null,
  },
  bestOfTempleton: null,
  documents: initialDocumentsState,
  transaction: null,
};

export interface FormState {
  verification: VerificationFormState;
  informations: {
    companyInfo: CompanyInfoFormValues | null;
    customerInfo1: ClientFormValues | null;
    customerInfo2: ClientFormValues | null;
  };
  investing: {
    agreements: AgreementsFormValues | null;
    analyse: FinanceAnalyseFormValues | null;
    experience: InvestingExperienceFormValues | null;
    finances: FinancesFormValues | null;
    hazard: HazardProfileFormValues | null;
    strategy: InvestingStrategy | null;
  };
  protocol: {
    consultantConfirmation: ConsultantConfirmationFormValues | null;
    customerConfirmation: ClientConfirmationFormValues | null;
    customerStatement: ClientStatementFormValues | null;
    goals: InvestingGoalsFormValues | null;
    initiation: ConsultancyInitiationFormValues | null;
  };
  agreement: {
    businessPartnerInfo: BusinessPartnerInfoFormValues | null;
    meeting: MeetingFormValues | null;
    requestInfo: RequestInfoFormValues | null;
  };
  bestOfTempleton: BestOfTempletonAccountFormValues | null;
  documents: DocumentsState;
  transaction: Transaction | null;
}

const informations = createReducer(initialFormState.informations, (builder) => {
  builder
    .addCase(setInformationsClient1InfoValuesPartialAction, (state, { payload }) => ({
      ...state,
      customerInfo1: {
        ...(state.customerInfo1 as ClientFormValues),
        ...payload,
      },
    }))
    .addCase(setInformationsCompanyInfoValuesAction, (state, action) => {
      state.companyInfo = action.payload;
    })
    .addCase(setInformationsClient1InfoValuesAction, (state, action) => {
      state.customerInfo1 = action.payload;
    })
    .addCase(setInformationsClient2InfoValuesAction, (state, action) => {
      state.customerInfo2 = action.payload;
    })
    .addCase(preloadFormStateAction, (_state, action) => action.payload.informations)
    .addCase(resetFormAction, () => initialFormState.informations);
});

const investing = createReducer(initialFormState.investing, (builder) => {
  builder
    .addCase(setInvestingExperienceValuesAction, (state, action) => {
      state.experience = action.payload;
    })
    .addCase(setInvestingHazardValuesAction, (state, action) => {
      state.hazard = action.payload;
    })
    .addCase(setInvestingFinancesValuesAction, (state, action) => {
      state.finances = action.payload;
    })
    .addCase(setInvestingAnalyseValuesAction, (state, action) => {
      state.analyse = action.payload;
    })
    .addCase(setInvestingAgreementsValuesAction, (state, action) => {
      state.agreements = action.payload;
    })
    .addCase(setInvestingStrategyAction, (state) => {
      state.strategy = calculateStrategy(state);
    })
    .addCase(resetFormAction, () => initialFormState.investing)
    .addCase(preloadFormStateAction, (_state, action) => action.payload.investing)
    .addCase(syncInvestingAgreementsWithProtocolAction, (state, action) => {
      if (state.agreements) {
        state.agreements.dateOfSignature = action.payload.date;
        state.agreements.interviewTime = action.payload.interviewDuration;
        state.agreements.placeOfSignature = action.payload.city;
      }
    });
});

const protocol = createReducer(initialFormState.protocol, (builder) => {
  builder
    .addCase(setProtocolGoalsValuesAction, (state, action) => {
      state.goals = action.payload;
    })
    .addCase(setProtocolInitiationValuesAction, (state, action) => {
      state.initiation = action.payload;
    })
    .addCase(updateProtocolInitiationValuesAction, (state, action) => {
      state.initiation = updateProtocolInitiationByStrategy(state.initiation, action.payload);
    })
    .addCase(setProtocolCustomerStatementValuesAction, (state, action) => {
      state.customerStatement = action.payload;
    })
    .addCase(setProtocolCustomerConfirmationValuesAction, (state, action) => {
      state.customerConfirmation = action.payload;
    })
    .addCase(setProtocolConsultantConfirmationValuesAction, (state, action) => {
      state.consultantConfirmation = action.payload;
    })
    .addCase(resetFormAction, () => initialFormState.protocol)
    .addCase(preloadFormStateAction, (_state, action) => action.payload.protocol);
});

const agreement = createReducer(initialFormState.agreement, (builder) => {
  builder
    .addCase(setAgreementRequestInfoAction, (state, action) => {
      state.requestInfo = action.payload;
    })
    .addCase(setAgreementMeetingAction, (state, action) => {
      state.meeting = action.payload;
    })
    .addCase(setAgreementBusinessPartnerInfoAction, (state, action) => {
      state.businessPartnerInfo = action.payload;
    })
    .addCase(resetFormAction, () => initialFormState.agreement)
    .addCase(preloadFormStateAction, (_state, action) => action.payload.agreement);
});

const transaction = createReducer(initialFormState.transaction, (builder) => {
  builder.addCase(loadTransactionActions.success, (_state, action) => action.payload);
});

const bestOfTempleton = createReducer(initialFormState.bestOfTempleton, (builder) => {
  builder
    .addCase(setBestOfTempletonAccountInfo, (_state, action) => action.payload)
    .addCase(preloadFormStateAction, (_state, action) =>
      action.payload.bestOfTempleton ? action.payload.bestOfTempleton : null,
    )
    .addCase(resetFormAction, () => initialFormState.bestOfTempleton);
});

export default combineReducers({
  verification,
  transaction,
  informations,
  investing,
  protocol,
  agreement,
  bestOfTempleton,
  documents,
});

// Shared helper
async function saveFormData(listenerApi: AppListenerEffectApi) {
  const state = listenerApi.getState();
  const { agreement, informations, investing, protocol, bestOfTempleton }: FormState = selectForm(state);
  const transaction: Transaction = selectFormTransaction(state)!;
  const resp = await api.transaction.edit(transaction._id, {
    protocol,
    informations,
    agreement,
    investing,
    bestOfTempleton,
  });
  if (resp.ok) {
    listenerApi.dispatch(preloadFormStateAction(resp.data.entries[0].meta));
    listenerApi.dispatch(loadTransactionActions.success(resp.data));

    const t = await i18n;
    message.success(t('formsManagement.saved'));
  }
}

// Listeners
startListening({
  actionCreator: loadTransactionActions.request,
  effect: async (action, listenerApi) => {
    listenerApi.cancelActiveListeners();
    const resp = await api.transaction.findOne(action.payload);
    if (resp.ok) {
      listenerApi.dispatch(preloadFormStateAction(resp.data.entries[0].meta));
      listenerApi.dispatch(loadTransactionActions.success(resp.data));
    }
  },
});

// Atomic form updates
startListening({
  matcher: isAnyOf(
    setInformationsCompanyInfoValuesAction,
    setInformationsClient1InfoValuesAction,
    setInformationsClient2InfoValuesAction,
    setInformationsClient1InfoValuesPartialAction,
    setInvestingAnalyseValuesAction,
    setInvestingAgreementsValuesAction,
    setProtocolGoalsValuesAction,
    setProtocolInitiationValuesAction,
    setProtocolCustomerStatementValuesAction,
    setProtocolCustomerConfirmationValuesAction,
    setProtocolConsultantConfirmationValuesAction,
    setBestOfTempletonAccountInfo,
  ),
  effect: async (_action, listenerApi) => {
    listenerApi.cancelActiveListeners();
    await saveFormData(listenerApi);
  },
});

// Multi-part form updates (strategy recalculation + save)
startListening({
  matcher: isAnyOf(
    setInvestingExperienceValuesAction,
    setInvestingHazardValuesAction,
    setInvestingFinancesValuesAction,
  ),
  effect: async (_action, listenerApi) => {
    listenerApi.cancelActiveListeners();
    listenerApi.dispatch(setInvestingStrategyAction());
    const strategy = selectInvestingStrategy(listenerApi.getState());
    listenerApi.dispatch(updateProtocolInitiationValuesAction(strategy!));
    await saveFormData(listenerApi);
  },
});

// Agreement: wait for business partner info, then save
startListening({
  actionCreator: setAgreementRequestInfoAction,
  effect: async (_action, listenerApi) => {
    listenerApi.cancelActiveListeners();
    await listenerApi.take(setAgreementBusinessPartnerInfoAction.match);
    await saveFormData(listenerApi);
  },
});
